import { Icon, Notification } from '@arcadiapower/shrike';
import { formatPhoneNumber } from '@arcadiapower/warbler';
import { useContext, useEffect } from 'react';
import { EditInformationDialog, ResetWarningDialog } from '~/components/dialogs';
import { Link } from '~/components/link';
import { Text } from '~/components/typography';
import type { TelesalesEditUserInfo } from '~/machines/telesales-funnel/types';
import type { User } from '~/typings/context';
import { deleteCookies } from '~/utils/for-warbler';
import {
  Wrapper,
  ShareButton,
  CustomerDetailsContainer,
  NameContainer,
  ContactInfoContainer,
  MarkCompletedButton,
  ButtonsContainer,
} from './customer-details.style';

export type Props = {
  buttonText: string;
  shareButtonHandler?: () => void;
  user: User;
  emailResult?: { totalSent: number; totalFailed: number; errorMessage?: string };
  onSubmitEditInformation: (data: TelesalesEditUserInfo) => void;
};

const EmailResultNotifier = ({
  emailResult,
}: {
  emailResult?: { totalSent: number; totalFailed: number; errorMessage?: string };
}) => {
  const { errorMessage } = emailResult || {};
  const { addNotification } = useContext(Notification.Context);

  const addSucessNotification = () => {
    addNotification({
      displayTime: 5000,
      message: 'Email has been sent to the customer!',
      variant: 'success',
    });
  };

  const addErrorNotification = () => {
    addNotification({
      displayTime: 5000,
      message: errorMessage || 'Please check the user details and try again.',
      variant: 'error',
    });
  };

  useEffect(() => {
    if (emailResult?.totalSent && emailResult.totalSent > 0) {
      addSucessNotification();
    } else if (emailResult?.totalFailed && emailResult.totalFailed > 0) {
      addErrorNotification();
    }
  }, [emailResult]);

  return null;
};

export const CustomerDetails = ({
  buttonText,
  shareButtonHandler,
  user,
  emailResult,
  onSubmitEditInformation,
}: Props) => {
  const { firstName, lastName, email, phone } = user;
  const fullName = `${firstName} ${lastName}`;
  const phoneNumber = formatPhoneNumber(phone || '');

  const handleMarkCompleted = () => {
    localStorage.clear();
    deleteCookies();
    location.reload();
  };

  return (
    <Wrapper
      gridColumn="auto / span 2"
      gridColumnGap="8px"
      display="flex"
      justifyContent="space-between"
      p={24}
      mb={28}
    >
      <CustomerDetailsContainer display="flex" flexDirection="row" gridColumnGap="24px">
        <NameContainer display="flex" flexDirection="column" gridRowGap="4px">
          <Text textStyle="heading700">{fullName}</Text>
          <Text color="accent9" textStyle="heading600">
            <EditInformationDialog
              user={user}
              target={
                <Link color="primary" testId="edit-user-info-button">
                  Edit information
                </Link>
              }
              handleSubmit={onSubmitEditInformation}
            />
          </Text>
        </NameContainer>
        <ContactInfoContainer display="flex" flexDirection="column">
          <Text>
            <Icon icon="Phone" margin={{ right: '12px' }} />
            {phoneNumber}
          </Text>
          <Text>
            <Icon icon="Envelope" margin={{ right: '12px' }} />
            {email}
          </Text>
        </ContactInfoContainer>
      </CustomerDetailsContainer>
      <ButtonsContainer>
        <ShareButton
          testId="share-with-customer-button"
          mode="default"
          fullWidth={false}
          onClick={shareButtonHandler}
        >
          <Icon icon="Link" margin={{ right: '8px' }} />
          {buttonText}
        </ShareButton>
        <ResetWarningDialog
          target={
            <MarkCompletedButton testId="mark-complete-button">Mark completed</MarkCompletedButton>
          }
          handleReset={handleMarkCompleted}
        />
      </ButtonsContainer>
      <EmailResultNotifier emailResult={emailResult} />
    </Wrapper>
  );
};
